import { forwardRef, type ChangeEvent, type ComponentPropsWithoutRef } from "react";
import { cn } from "../../utils/cn";

type DSCheckboxProps = Omit<ComponentPropsWithoutRef<"input">, "type"> & {
  invalid?: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

const DSCheckbox = forwardRef<HTMLInputElement, DSCheckboxProps>(function DSCheckbox(
  { checked, className, defaultChecked, invalid = false, onChange, onCheckedChange, ...props },
  ref
) {
  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    onChange?.(event);
    onCheckedChange?.(event.currentTarget.checked);
  }

  return (
    <span className="relative inline-flex size-5 shrink-0 items-center justify-center">
      <input
        checked={checked}
        className={cn(
          "peer m-0 size-5 cursor-pointer appearance-none rounded-sm border bg-body-light-mode-1 transition-[background-color,border-color,opacity] duration-150 ease-out checked:bg-brand-primary-3 checked:border-brand-primary-3 disabled:cursor-not-allowed disabled:border-gray-3 disabled:bg-gray-2",
          invalid ? "border-feedback-danger" : "border-gray-5",
          className
        )}
        defaultChecked={defaultChecked}
        onChange={handleChange}
        ref={ref}
        type="checkbox"
        {...props}
      />
      <svg
        aria-hidden
        className="pointer-events-none absolute size-3 text-body-light-mode-1 opacity-0 transition-opacity duration-150 ease-out peer-checked:opacity-100 peer-disabled:text-gray-4"
        fill="none"
        viewBox="0 0 12 12"
      >
        <path
          d="M2.5 6.25 4.75 8.5 9.5 3.75"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    </span>
  );
});

DSCheckbox.displayName = "DSCheckbox";

export { DSCheckbox };
export type { DSCheckboxProps };
